import React, { useMemo, useEffect } from 'react';
import { Slate, withReact } from 'slate-react';
import { createEditor } from 'slate';
import { withHistory } from 'slate-history';
import Link from '@material-ui/core/Link';
import { withLinks } from 'pages/Homepage/components/WidgetEdit/widgetSettings/RichTextWidgetSettings/helpers/linkHelpers';
import withDeleteEmptyBlock from './helpers/withDeleteEmptyBlock';
import getRichTextCharCount from 'pages/Homepage/components/WidgetEdit/widgetSettings/RichTextWidgetSettings/helpers/getRichTextCharCount';
import resetRichText from 'pages/Homepage/components/WidgetEdit/widgetSettings/RichTextWidgetSettings/helpers/resetRichText';
import Typography from '@material-ui/core/Typography';
import CardContent from '@material-ui/core/CardContent';
import FormHelperText from '@material-ui/core/FormHelperText';
import CharCount from 'atoms/CharCount';
import Card from 'pages/Homepage/components/molecules/WidgetConfigCard';
import SlateWrapper from './SlateWrapper';
import EditArea from './EditArea';
import Toolbar from './Toolbar';
import HeadingOneButton from './HeadingOneButton';
import HeadingTwoButton from './HeadingTwoButton';
import HeadingThreeButton from './HeadingThreeButton';
import BoldButton from './BoldButton';
import ItalicButton from './ItalicButton';
import UnderlineButton from './UnderlineButton';
import StrikeThroughButton from './StrikeThroughButton';
import OrderedListButton from './OrderedListButton';
import UnorderedListButton from './UnorderedListButton';
import LinkButton from './LinkButton';
import { MAX_CHAR_COUNT_RICH_TEXT } from 'pages/Homepage/helpers/consts';

interface Props {
  value: Array<any>;
  originalValue: Array<any>;
  onSetFieldValue: (fieldKey: string, fieldValue: string) => void;
  shouldReset?: boolean;
  errors?: any;
  resetShouldReset: () => void;
}

function RichTextExpanded({
  value,
  originalValue,
  onSetFieldValue,
  shouldReset,
  errors,
  resetShouldReset,
}: Props) {
  const handleChange = (editorValue: any) => {
    onSetFieldValue('settings.richTextExpanded', editorValue);
  };

  const count = getRichTextCharCount(value);

  const editor = useMemo(
    () => withDeleteEmptyBlock(withLinks(withReact(withHistory(createEditor())))),
    [],
  );

  useEffect(() => {
    if (shouldReset) {
      resetRichText(editor, originalValue);
      resetShouldReset();
    }
  }, [shouldReset]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h5">Alternate Expanded View Content (Optional)</Typography>
        <Typography variant="subtitle2">
          This rich text content will appear only in the expanded, larger view. If you have longer
          content that you’d like to appear when the widget is expanded, include it here. If this
          section is not filled out, the default content above will appear in both the default and
          expanded widget view. On your homepage, hyperlinks will use your highlight color from your{' '}
          <Link href={'/admin/general/theming '} color="secondary">
            theme
          </Link>
          .
        </Typography>

        <SlateWrapper>
          <Slate editor={editor} initialValue={value} onChange={handleChange}>
            <Toolbar>
              <HeadingOneButton />
              <HeadingTwoButton />
              <HeadingThreeButton />
              <BoldButton />
              <ItalicButton />
              <UnderlineButton />
              <StrikeThroughButton />
              <OrderedListButton />
              <UnorderedListButton />
              <LinkButton editor={editor} />
            </Toolbar>
            <EditArea editor={editor} />
          </Slate>
        </SlateWrapper>
        {errors.richTextExpanded && (
          <FormHelperText error>{errors.richTextExpanded}</FormHelperText>
        )}
        <CharCount count={count} maxCount={MAX_CHAR_COUNT_RICH_TEXT} />
      </CardContent>
    </Card>
  );
}

export default RichTextExpanded;
